import { useId } from "react";
import type { BaseDataPoint } from "../../../utils/charts";
import type { AssembledSeries } from "../../../utils/timeSeriesTiles";

/**
 * Which plotted series the readout should inspect.
 *
 * ── Why a named kind and not a function ───────────────────────────────────────
 * A streaming chart can be driven from stored JSON, and a render-prop is exactly
 * what such a caller cannot hold. The set of things worth reading "latest data"
 * out of is small and closed — a single plotted series, a net band plus its
 * average line, or an import/export pair — so naming them costs nothing and buys
 * the whole affordance for a declarative caller.
 *
 * `single` is the general case and the others are named for the shape they read,
 * not for the domain that happens to use them: this file knows nothing about
 * meters.
 */
export type LatestDataReadoutKind = "single" | "band" | "pair";

export interface LatestDataReadoutProps {
  kind: LatestDataReadoutKind;
  series: AssembledSeries;
  viewport: { startMs: number; endMs: number };
  maxDataTimeMs: number;
  /** Which streams `pair` and `band` read, in draw order. Defaults per kind. */
  seriesKeys?: string[];
}

const DEFAULT_KEYS: Record<LatestDataReadoutKind, string[]> = {
  single: [],
  band: ["net"],
  pair: ["import", "export"],
};

/**
 * The plotted points this readout is allowed to consider.
 *
 * 🔴 Only the SELECTED plot, never source-wide provenance. A chart showing the
 * net view must not report a timestamp that only the gross series reaches — the
 * reader would see a "latest data" time for something not on screen.
 */
function plottedFor({ kind, series, seriesKeys }: Pick<LatestDataReadoutProps, "kind" | "series" | "seriesKeys">) {
  const keys = seriesKeys ?? DEFAULT_KEYS[kind];
  if (kind === "single") return [series.line];
  if (kind === "band") {
    return [
      ...keys.map((key) => series.lines[key] ?? []),
      // Only band points carrying a real baseline are drawn as a range; the
      // rest are gaps and must not date the readout.
      series.band.filter((point) => Number.isFinite(point.baselineValue)),
    ];
  }
  return keys.map((key) => series.lines[key] ?? []);
}

/**
 * The latest timestamp actually drawn in the visible window, or `null`.
 *
 * Exported because the rule is worth asserting directly: the bounds are
 * deliberately asymmetric, and that is the kind of thing a rendered assertion
 * cannot pin down.
 */
export function latestVisibleDataTime(props: LatestDataReadoutProps): number | null {
  const { viewport, maxDataTimeMs } = props;
  let latest: number | null = null;
  for (const plotted of plottedFor(props)) {
    for (const point of plotted as BaseDataPoint[]) {
      const timestamp = point.xValue.getTime();
      if (
        Number.isFinite(timestamp) &&
        Number.isFinite(point.yValue) &&
        // `viewport.endMs` is EXCLUSIVE and `maxDataTimeMs` INCLUSIVE — the
        // first is a window edge, the second a fetch cutoff. Assembled series
        // carry overscan beyond the viewport, so without this clamp the readout
        // reports data the reader cannot see.
        timestamp >= viewport.startMs &&
        timestamp < viewport.endMs &&
        timestamp <= maxDataTimeMs &&
        (latest === null || timestamp > latest)
      ) {
        latest = timestamp;
      }
    }
  }
  return latest;
}

/**
 * "Latest data in view: …" — how current the plotted window actually is.
 *
 * Reads the drawn points rather than the source, because those are two
 * different questions: a source can hold readings the current window does not
 * show, and the reader is asking about what is on screen.
 */
export function LatestDataReadout(props: LatestDataReadoutProps) {
  const descriptionId = useId();
  const latest = latestVisibleDataTime(props);
  const date = latest === null ? null : new Date(latest);
  return (
    <span
      className="inline-flex max-w-full flex-wrap items-baseline gap-x-1"
      aria-describedby={descriptionId}
      title="Time marks the start of the latest interval with data plotted in this view."
    >
      <span>Latest data in view:</span>
      {date ? (
        <time dateTime={date.toISOString()} className="font-medium text-text-primary">
          {date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
          })}
        </time>
      ) : (
        <span className="font-medium text-text-primary">Unavailable</span>
      )}
      <span id={descriptionId} className="sr-only">
        Time marks the start of the latest interval with data plotted in this view.
      </span>
    </span>
  );
}
